import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import AdminLayout from "@/components/layout/AdminLayout";
import CampOpsNav from "@/components/CampOpsNav";
import api from "@/lib/api";
import { Activity, RefreshCw, Zap, Crosshair, Timer, Flame, Star } from "lucide-react";

export default function CampLiveDashboard() {
  const { campId } = useParams();
  const [data, setData] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);

  const load = useCallback(() => {
    api.get(`/admin/camps/${campId}/live-dashboard`).then(({ data }) => {
      setData(data);
      setUpdatedAt(new Date());
    }).catch(() => {});
  }, [campId]);

  useEffect(() => {
    load();
    const t = setInterval(load, 8000); // real-time refresh
    return () => clearInterval(t);
  }, [load]);

  if (!data) return <AdminLayout><div className="p-8 label-overline">Loading dashboard…</div></AdminLayout>;

  const a = data.attendance;
  const tiles = [
    { label: "Registered", val: a.registered, color: "text-slate-600" },
    { label: "Checked In", val: a.checked_in, color: "text-emerald-700" },
    { label: "Late", val: a.late, color: "text-amber-700" },
    { label: "No Shows", val: a.no_show, color: "text-red-700" },
    { label: "Total", val: a.total, color: "text-[#0A192F]" },
  ];

  const leaders = [
    { key: "exit_velocity", label: "Top Exit Velo", icon: Zap, data: data.leaders.exit_velocity },
    { key: "arm_velocity", label: "Top Arm Velo", icon: Crosshair, data: data.leaders.arm_velocity },
    { key: "pop_time", label: "Top Pop Time", icon: Timer, data: data.leaders.pop_time },
    { key: "fastball_velocity", label: "Top Fastball", icon: Flame, data: data.leaders.fastball_velocity },
    { key: "cbr_rating", label: "Top CBR Rating", icon: Star, data: data.top_rating, rating: true },
  ];

  return (
    <AdminLayout>
      <div className="p-8 max-w-7xl">
        <Link to="/admin/camps" className="label-overline text-slate-500 hover:text-[#E63946]">← All Camps</Link>
        <div className="flex flex-wrap items-end justify-between mt-3 gap-3">
          <div>
            <div className="label-overline text-[#E63946]">Live Camp Dashboard</div>
            <h1 data-testid="live-dashboard-title" className="font-display text-4xl uppercase tracking-tighter mt-2">{data.camp.title}</h1>
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <Activity className="w-4 h-4 text-emerald-600 animate-pulse" /> Live · updated {updatedAt?.toLocaleTimeString("en-CA")}
            <button data-testid="live-refresh" onClick={load} className="ml-2 p-1.5 border border-slate-300 rounded-sm hover:bg-slate-50"><RefreshCw className="w-4 h-4" /></button>
          </div>
        </div>

        <CampOpsNav campId={campId} />

        {/* Attendance */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {tiles.map((t) => (
            <div key={t.label} data-testid={`live-tile-${t.label.toLowerCase().replace(/ /g, '-')}`} className="bg-white border border-slate-200 p-4 rounded-sm">
              <div className="label-overline">{t.label}</div>
              <div className={`font-data text-4xl mt-1 ${t.color}`}>{t.val}</div>
            </div>
          ))}
        </div>

        {/* Leaders */}
        <h2 className="font-display text-2xl uppercase tracking-tight mt-10">Live Leaders</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
          {leaders.map((l) => (
            <div key={l.key} data-testid={`live-leader-${l.key}`} className="bg-[#0A192F] text-white p-5 rounded-sm">
              <div className="flex items-center justify-between">
                <div className="label-overline text-[#E63946]">{l.label}</div>
                <l.icon className="w-5 h-5 text-white/40" />
              </div>
              {l.data?.player ? (
                <>
                  <div className="font-data text-4xl mt-3">
                    {l.rating ? Number(l.data.value).toFixed(1) : l.data.value}
                    {!l.rating && l.data.units && <span className="text-lg ml-1 text-white/50">{l.data.units}</span>}
                  </div>
                  <Link to={`/prospects/${l.data.player.slug}`} className="text-sm mt-2 inline-block hover:text-[#E63946]">
                    {l.data.player.first_name} {l.data.player.last_name}
                    <span className="text-white/50 ml-1">· {l.data.player.primary_position || "—"}</span>
                  </Link>
                </>
              ) : (
                <div className="text-sm text-white/50 mt-4">No results yet</div>
              )}
            </div>
          ))}
        </div>
      </div>
    </AdminLayout>
  );
}
